import { Injectable, inject } from '@angular/core';
import {
  DetectionService,
  RenderService,
  SimulationService,
  TaskService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { ExtraCorporealKnotMetricsService } from './extracorporeal-knot-metrics.service';
import { SharedService } from './shared.service';

const WORKERS = {
  cycleWorker: {
    id: 'cycleWorker',
    model: 'default',
    historySize: 20,
    confidence: 0.2,
  },
  piercingClassWorker: {
    id: 'piercingClassWorker',
    model: 'piercingClassification',
    historySize: 20,
    confidence: 0.1,
  },
};

@Injectable({
  providedIn: 'root',
})
export class ExtraCorporealKnotRightService {
  private extracorporealKnotMetricsService = inject(
    ExtraCorporealKnotMetricsService,
  );
  private detectionService = inject(DetectionService);
  private renderService = inject(RenderService);
  private shared = inject(SharedService);
  private simulationService = inject(SimulationService);
  private taskService = inject(TaskService);

  constructor() {
    this.detectionService.addWorkers(WORKERS);
    this.shared.cycleWorkerId = WORKERS.cycleWorker.id;
    this.shared.piercingClassWorkerId = WORKERS.piercingClassWorker.id;
  }

  async runDetection() {
    if (this.simulationService.isSimulationRunning()) {
      await this.detectionService.detect(WORKERS.cycleWorker.id);
      if (this.simulationService.state() === 'kit') {
        this.shared.checkSample();
      } else if (this.simulationService.state() === 'simulation') {
        this.shared.toolTracking();
        switch (this.taskService.currentTaskId()) {
          case 'checkSample': {
            this.shared.checkSample().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.renderService.waitForStaticCleanup();
                await this.taskService.completeTask('checkSample');
              }
            });
            break;
          }
          case 'checkInstruments': {
            this.shared.checkInstruments().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.renderService.waitForStaticCleanup();
                await this.taskService.completeTask('checkInstruments');
              }
            });
            break;
          }
          case 'piercePenrose': {
            await this.detectionService.detect(WORKERS.piercingClassWorker.id);
            this.shared.piercePenrose().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.taskService.completeTask('piercePenrose');
              }
            });
            break;
          }
          case 'pullSuture': {
            await this.detectionService.detect(WORKERS.piercingClassWorker.id);
            this.shared.pullSuture().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.taskService.completeTask('pullSuture');
              }
            });
            break;
          }
          case 'tieKnots': {
            this.shared.tieKnots().then(async (isConfirmed) => {
              if (isConfirmed) {
                return;
              }
            });
            break;
          }
          case 'cutThreads': {
            if (!this.taskService.taskListCompleted()) {
              this.shared.cutThreads().then(async (isConfirmed) => {
                if (isConfirmed) {
                  await this.taskService.completeTask('cutThreads');
                }
              });
            }
            break;
          }
        }
      }
    }
  }

  stopDetection() {
    this.extracorporealKnotMetricsService.finalizeMetrics();
    this.detectionService.terminateAllworkers();
  }
}
